import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WorkbenchNotice } from '@ndrc/ui';
import { callCore } from '../lib/rpc';
import { t } from '../messages';
import { dateLabel, type Criterion, type Review } from './model';
export function PracticeFeedback({
  classId,
  assignmentId,
  submissionId,
  rubric,
}: {
  classId: string;
  assignmentId: string;
  submissionId: string;
  rubric: Criterion[];
}) {
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const query = useQuery({
    queryKey: ['core', 'practice.reviews', classId, assignmentId, submissionId, cursor],
    queryFn: () =>
      callCore<{ items: Review[]; nextCursor: string | null }>('practice.reviews', {
        classId,
        assignmentId,
        submissionId,
        cursor,
      }),
    refetchInterval: cursor ? false : 10000,
    retry: false,
  });
  return (
    <section aria-label={t('practice.history.title')}>
      <h3>{t('practice.history.title')}</h3>
      <p>{t('practice.history.hint')}</p>
      <div className="lab-actions">
        <button
          type="button"
          disabled={query.isFetching}
          onClick={() => {
            void query.refetch();
          }}
        >
          {t('practice.history.refresh')}
        </button>
        {cursor && (
          <button type="button" disabled={query.isFetching} onClick={() => setCursors([])}>
            {t('practice.history.latest')}
          </button>
        )}
      </div>
      {query.isPending && <p role="status">{t('practice.loading')}</p>}
      {query.isError && <WorkbenchNotice error>{query.error.message}</WorkbenchNotice>}
      {!query.isPending && !query.isError && !query.data?.items.length && (
        <p>{t('practice.history.empty')}</p>
      )}
      {query.data?.items.map((review) => (
        <article className="practice-feedback" key={review.id}>
          <strong>{review.authorName}</strong>
          <span className="practice-badge">
            {t(review.published ? 'practice.feedbackPublished' : 'practice.feedbackPrivate')}
          </span>
          <p>
            <time dateTime={review.createdAt}>{dateLabel(review.createdAt)}</time>
          </p>
          <p className="practice-prose">{review.note}</p>
          <ul>
            {review.scores.map((score) => (
              <li key={score.criterionId}>
                {rubric.find((c) => c.id === score.criterionId)?.label ?? score.criterionId}
                <span>
                  {score.points === null
                    ? t('practice.noGrade')
                    : t('practice.points', { count: score.points })}
                </span>
              </li>
            ))}
          </ul>
        </article>
      ))}
      {(cursor || query.data?.nextCursor) && (
        <nav className="lab-actions" aria-label={t('practice.history.title')}>
          <button
            type="button"
            disabled={!cursor || query.isFetching}
            onClick={() => setCursors(cursors.slice(0, -1))}
          >
            {t('practice.history.newer')}
          </button>
          <span role="status">{t('practice.history.page', { count: cursors.length + 1 })}</span>
          <button
            type="button"
            disabled={!query.data?.nextCursor || query.isFetching || query.isError}
            onClick={() => {
              if (query.data?.nextCursor) setCursors([...cursors, query.data.nextCursor]);
            }}
          >
            {t('practice.history.older')}
          </button>
        </nav>
      )}
    </section>
  );
}
